import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryArgs,
  FetchBaseQueryError,
  retry,
  RetryOptions,
} from '@reduxjs/toolkit/query';

import { apiUrls } from '@Lib/redux/urls';
import { getSubdomainAndTldFromHost } from '@Lib/utils';

export const retryOptions: RetryOptions = {
  retryCondition: (error, _arguments__, extraArguments) => {
    if ((error as any).status === 401) return false;

    const maxAttempts = process.env.NODE_ENV === 'development' ? 0 : 2;

    if (extraArguments.attempt <= maxAttempts) {
      return true;
    }
    return false;
  },
};

export const validateErrorStatus: FetchBaseQueryArgs['validateStatus'] = (response: Response) => {
  return response.status >= 200 && response.status <= 299;
};

export const getApiBaseUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const { tld: urlTld } = getSubdomainAndTldFromHost(hostname);
  const isAt = urlTld === 'at';

  // Local Next rewrite (/api → Nest). SSO login uses this as a full-page URL
  return process.env.NODE_ENV === 'development'
    ? '/api' // use Next.js rewrite proxy in dev
    : isAt // real API in deployment
      ? process.env.NEXT_PUBLIC_BACKEND_ENDPOINT_AT + '/'
      : process.env.NEXT_PUBLIC_BACKEND_ENDPOINT_DE + '/';
};

/** Password login rejects bad credentials with 401. That must not reload /login. */
export const isCredentialLoginRequest = (args: string | FetchArgs): boolean => {
  const url = typeof args === 'string' ? args : args.url;
  const path = url.split('?')[0].replace(/\/+$/, '');
  return path === apiUrls.auth.login || path.endsWith(`/${apiUrls.auth.login}`);
};

export const retryingBaseQuery = (
  baseUrl?: string,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const resolvedBaseUrl = baseUrl ?? getApiBaseUrl();

  const baseQuery = fetchBaseQuery({
    baseUrl: resolvedBaseUrl,
    credentials: 'include',
    validateStatus: validateErrorStatus,
  });

  return async (args, api, extraOptions) => {
    const result = await retry(baseQuery, retryOptions)(args, api, extraOptions);

    if (result.error?.status === 401 && !isCredentialLoginRequest(args)) {
      try {
        const url =
          process.env.NODE_ENV === 'development'
            ? resolvedBaseUrl + '/' + apiUrls.auth.logout
            : resolvedBaseUrl + apiUrls.auth.logout;

        await fetch(url, {
          method: 'POST',
          credentials: 'include',
        });
      } catch {}

      // Dispatch logout action directly to avoid circular dependency
      api.dispatch({ type: 'user/logout' });
      window.location.href = '/login';
    }

    return result;
  };
};
